// @flow
import React, { useState, useEffect, useRef } from 'react';
import injectSheet from 'react-jss';
import cx from 'classnames';
import SocialIcon from 'shared/components/SocialIcon';
import CTABlock from './CTABlock';
import type { Title } from './CTABlock';
import useIntersectionObserver from 'shared/hooks/useIntersectionObserver';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type InternalVideoProps = {
  autoplay: boolean,
  loop: boolean,
  muted: boolean,
  controls: boolean,
  downloadable: boolean,
};

type Props = {
  classes: Object,
  video_src: string,
  cover_image: string,
  caption: string,
  internal_video_props: InternalVideoProps,
  title_props: Title,
  youtube_link: string,
  social_share: Object,
  padding_type: 'Non-FutureSight Padding' | 'FutureSight Padding',
  custom_image_padding: string,
  intersection_observer_threshold: string,
  color_theme: string
};

const getControlsList = (internalVideoProps: InternalVideoProps) => {
  const { downloadable = true } = internalVideoProps;

  const controls = ['noremoteplayback'];

  if (!downloadable) controls.push('nodownload');

  return controls.join(' ');
};

const VideoPlayer = (props: Props) => {
  const {
    classes: c,
    video_src: videoSrc,
    cover_image: coverImage,
    caption,
    internal_video_props: internalVideoProps = {},
    title_props: titleProps = {},
    youtube_link: youtubeLink,
    social_share: socialShare,
    intersection_observer_threshold
  } = props;

  const {
    show_social_icons: showSocialIcons,
    color_theme: socialColorTheme,
    qr_code_url: socialQrCodeUrl,
    social_media_icons: socialIconData,
  } = socialShare || {};

  const {
    autoplay = false,
    loop = false,
    muted = false,
    controls = true,
    downloadable = true,
  } = internalVideoProps;

  // Remove margins to prevent white line on mobile - inline video with no controls.
  const removeMargins = !controls;

  const ref = useRef<HTMLVideoElement | null>(null);
  const isIntersecting = useIntersectionObserver(ref, '0px', intersection_observer_threshold || 0);

  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  // Load video if it's in viewpoint
  useEffect(() => {
    const loadVideo = () => {
      if (isIntersecting) {
        setIsLoadingVideo(true);
      }
    };

    loadVideo();
    document.addEventListener('scroll', loadVideo, { once: true });

    return () => {
      document.removeEventListener('scroll', loadVideo, { once: true });
    };
  }, [isIntersecting, setIsLoadingVideo]);

  // DDEV-3609: Enable autoplay in WeChat browser
  useEffect(() => {
    const play = () => {
      if (ref && ref.current && autoplay) {
        ref.current.play();
      }
    };

    document.addEventListener('WeixinJSBridgeReady', play, false);
    // Add handler for Android
    document.addEventListener('click', play, { once: true });

    return () => {
      document.removeEventListener('WeixinJSBridgeReady', play, false);
    };
  }, [ref, autoplay]);

  const video = videoSrc && (
    <video
      x5-video-player-type="h5"
      playsInline
      autoPlay={autoplay}
      loop={loop}
      muted={autoplay || muted}
      controls={controls}
      className={c.video}
      poster={coverImage}
      controlsList={getControlsList(internalVideoProps)}
      disablePictureInPicture
      onContextMenu={(e) => {
        if (!downloadable) {
          e.preventDefault();
        }
      }}
      // Safari: cover image is intermittently removed if video gets preloaded
      preload={coverImage ? 'none' : 'auto'}
      ref={ref}
    >
      {!isLoadingVideo && <source type="video/mp4" />}
      {isLoadingVideo && <source src={videoSrc} type="video/mp4" />}
    </video>
  );
  const youtubeId = youtubeLink.split('v=')[1];
  const youtubePlayer = !videoSrc && youtubeLink && (
    <div className={c.youtubeWrapper}>
      <iframe
        title="Faraday Future"
        src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
        frameBorder="0"
        allowFullScreen
      />
    </div>
  );
  const iconStyle = {
    fontSize: '28px',
    color: socialColorTheme === 'DARK' ? 'black' : 'white',
    marginLeft: '8px',
    marginRight: '8px',
    textDecoration: 'none',
    cursor: 'pointer',
  };
  const socialIcons = showSocialIcons ? (
    <div className={c.socialIcons}>
      <p className={c.share}>{`Share: `}</p>
      {socialIconData &&
        socialIconData.map((link, i) => (
          <SocialIcon
            key={i}
            iconStyle={iconStyle}
            link={link}
            generateQRCode={socialQrCodeUrl}
          />
        ))}
    </div>
  ) : null;

  return (
    <div className={cx(c.container, removeMargins && c.removeMargins)}>
      <div className={c.backgroundContainer}>
        {video}
        {youtubePlayer}
        <CTABlock titleProps={titleProps} />
        {caption && <div className={c.caption}>{caption}</div>}
        {socialIcons}
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: 'auto',
    position: 'relative',
  },
  backgroundContainer: {
    // Handle in inner div to prevent stretching to FlexibleColumns height
    margin: ({ padding_type = 'FutureSight Padding', custom_image_padding }) =>
      padding_type === 'FutureSight Padding'
        ? '26px 36px'
        : custom_image_padding || '0px',
    backgroundColor: ({ color_theme }) => color_theme === 'DARK' ? 'black' : 'white',
  },
  youtubeWrapper: {
    width: '100%',
    position: 'relative',
    paddingBottom: '56.25%',
    paddingTop: '25px',
    height: '0',
    '& > iframe': {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
    },
  },
  removeMargins: {
    overflow: 'hidden',
    '& > video': {
      width: '101%',
    }
  },
  video: {
    /*
      The video ref needs to be detected for the video to play.
      However, video ref may be getting skipped over or is not being
      detected during the page render. Add a min-height and min-width
      so there is at least a small bit of content when the video
      is being rendered for the first time.
    */
    minHeight: '1px',
    minWidth: '1px',
    display: 'block',
    width: '100%',
    objectFit: 'cover',
    // Default background - logo
    backgroundColor: ({ color_theme }) => color_theme  === 'DARK' ? 'black' : 'rgb(250, 250, 250)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '10%',
    backgroundImage: 'url(\'data:image/svg+xml;base64,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\')'
  },
  caption: {
    fontFamily: FONT_FAMILY_ROBOTO,
    fontSize: '17px',
    fontWeight: '600',
    lineHeight: '1.2',
  },
  share: {
    color: ({ social_share }) => social_share?.color_theme === 'DARK' ? 'black' : 'white',
    marginLeft: '5px',
  },
  socialIcons: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '20px',
  },
};

export default injectSheet(styles)(VideoPlayer);
